import { z } from "zod";
import { QuestionType, ScoringMode, PointsMode } from "../state/phases.js";
import { normalizeTypedAnswer } from "../scoring/engine.js";
export const AnswerOptionSchema = z.object({
    id: z.string().uuid(),
    label: z.string().min(1).max(200),
    isCorrect: z.boolean(),
});
export const QuizSettingsSchema = z.object({
    title: z.string().min(1).max(200).transform((s) => s.trim()),
    description: z.string().max(1000).optional(),
    randomizeOrder: z.boolean().default(false),
    showLeaderboard: z.boolean().default(true),
});
const baseQuestion = z.object({
    id: z.string().uuid(),
    imageAssetId: z.string().uuid().optional(),
    scoringMode: z.nativeEnum(ScoringMode).default(ScoringMode.CLASSIC),
    pointsMode: z.nativeEnum(PointsMode).default(PointsMode.STANDARD),
});
export const QuizSingleQuestionSchema = baseQuestion.extend({
    type: z.literal(QuestionType.QUIZ_SINGLE),
    prompt: z.string().min(1).max(2000),
    timerSeconds: z.number().int().min(5).max(240).default(30),
    options: z
        .array(AnswerOptionSchema)
        .min(2)
        .max(6)
        .refine((opts) => opts.filter((o) => o.isCorrect).length === 1, "Exactly one correct answer required"),
});
export const QuizMultiQuestionSchema = baseQuestion.extend({
    type: z.literal(QuestionType.QUIZ_MULTI),
    prompt: z.string().min(1).max(2000),
    timerSeconds: z.number().int().min(5).max(240).default(30),
    options: z
        .array(AnswerOptionSchema)
        .min(2)
        .max(6)
        .refine((opts) => opts.filter((o) => o.isCorrect).length >= 1, "At least one correct answer required"),
});
export const TrueFalseQuestionSchema = baseQuestion.extend({
    type: z.literal(QuestionType.TRUE_FALSE),
    prompt: z.string().min(1).max(2000),
    timerSeconds: z.number().int().min(5).max(240).default(15),
    options: z.array(AnswerOptionSchema).length(2),
});
/**
 * An accepted answer for a type_answer question.
 *
 * Scoring compares a player's submission against these values after running it
 * through `normalizeTypedAnswer`, so the stored value must already be in that
 * form — an un-normalized "Paris" can never match exactly. Normalizing here
 * rather than in the editor keeps that invariant true for every writer of the
 * API, not just the one client that happens to normalize first.
 */
export const AcceptedAnswerSchema = z.object({
    normalizedValue: z
        .string()
        .max(200)
        .transform(normalizeTypedAnswer)
        .refine((s) => s.length > 0, "Accepted answer cannot be empty"),
});
/** Accepted answers with blanks and duplicates removed after normalization. */
export const AcceptedAnswerListSchema = z
    .array(AcceptedAnswerSchema)
    .transform((answers) => {
    const seen = new Set();
    return answers.filter(({ normalizedValue }) => {
        if (seen.has(normalizedValue))
            return false;
        seen.add(normalizedValue);
        return true;
    });
});
export const TypeAnswerQuestionSchema = baseQuestion.extend({
    type: z.literal(QuestionType.TYPE_ANSWER),
    prompt: z.string().min(1).max(2000),
    timerSeconds: z.number().int().min(5).max(240).default(30),
    acceptedAnswers: AcceptedAnswerListSchema.refine((answers) => answers.length >= 1, "At least one accepted answer required"),
});
export const SlideQuestionSchema = baseQuestion.extend({
    type: z.literal(QuestionType.SLIDE),
    prompt: z.string().max(2000).optional(),
    content: z.string().max(5000).optional(),
});
export const QuestionSchema = z.discriminatedUnion("type", [
    QuizSingleQuestionSchema,
    QuizMultiQuestionSchema,
    TrueFalseQuestionSchema,
    TypeAnswerQuestionSchema,
    SlideQuestionSchema,
]);
//# sourceMappingURL=quiz.js.map